// Cut To Length Optimiser — core types, profile catalogue, and the
// kerf-aware First-Fit-Decreasing algorithm. Pure client-side, no server
// call. Ported from the Drillcut Tools Hub optimiser; UI-free by design so
// it can be unit-tested under the node vitest environment.

export interface CutRow {
  id: number;
  length: string;
  qty: string;
}

export interface MultiCutRow {
  id: number;
  section: string; // profile stock code
  length: string;
  qty: string;
}

export interface CutDef {
  len: number;
  qty: number;
}

export interface CutPiece {
  len: number;
  defIdx: number;
}

export interface StockBar {
  cuts: CutPiece[];
}

// Single-profile results (Standard mode)
export interface Results {
  stocks: StockBar[];
  cutDefs: CutDef[];
  totalPiece: number;
  totalStock: number;
  totalKerfs: number;
  totalWaste: number;
  yieldPct: number;
  stockLen: number;
  kerf: number;
  matDesc: string;
  profileLabel: string;
  profileCode: string;
}

// Per-section results for multi-profile mode
export interface SectionResult {
  profileName: string;
  profileCode: string;
  category: string;
  stockLen: number;
  kerf: number;
  stocks: StockBar[];
  cutDefs: CutDef[];
  totalPiece: number;
  totalStock: number;
  totalKerfs: number;
  totalWaste: number;
  yieldPct: number;
}

export interface MultiProfileResults {
  sections: SectionResult[];
  kerf: number;
  multiplier: number;
  grandTotalLengths: number;
  grandTotalWaste: number;
  grandAvgUtilisation: number;
}

export interface ProfileOption {
  code: string;
  label: string;
  category: "Mega" | "Strut" | "Angle" | "Allthread";
  stockLength: number;
}

export type Mode = "standard" | "multi";

// ── Constants ────────────────────────────────────────────────────────────────

// Segment colours for the bar-layout diagrams. First pair is the BSP brand
// blue; the rest are distinct hues that survive both screen and print.
export const COLORS = [
  "#1B449C",
  "#2d6a4f",
  "#92400e",
  "#1a3a5c",
  "#be185d",
  "#0369a1",
  "#065f46",
  "#b45309",
  "#6d28d9",
  "#9d174d",
];
export const LIGHT_COLORS = [
  "#E6ECF6",
  "#d1fae5",
  "#fef3c7",
  "#dbeafe",
  "#fce7f3",
  "#e0f2fe",
  "#d1fae5",
  "#fffbeb",
  "#f3e8ff",
  "#fdf2f8",
];

export const PROFILES: ProfileOption[] = [
  // Mega
  { code: "42PMBS050", label: "Mega Beam Section 50 x 50mm", category: "Mega", stockLength: 6000 },
  { code: "42PMBS080", label: "Mega Beam Section 80 x 80mm", category: "Mega", stockLength: 6000 },
  { code: "42PMBS100", label: "Mega Beam Section 100 x 100mm", category: "Mega", stockLength: 6000 },
  { code: "42PMBS120", label: "Mega Beam Section 100 x 120mm", category: "Mega", stockLength: 6000 },
  { code: "42PMBS150", label: "Mega Beam Section 100 x 150mm", category: "Mega", stockLength: 6000 },
  // Strut
  { code: "19D1000", label: "Plain Strut Galv 40 x 40 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D1000HDG", label: "Plain Strut HDG 40 x 40 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D1000SS", label: "Plain Strut S/S 40 x 40 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D1000T", label: "Slotted Strut Galv 40 x 40 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D1000THDG", label: "Slotted Strut HDG 40 x 40 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D1000TSS", label: "Slotted Strut S/S 40 x 40 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D1001", label: "Plain Strut HDG (B2B) 40 x 80 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D2000", label: "Plain Strut Galv 40 x 40 x 1.6mm", category: "Strut", stockLength: 6000 },
  { code: "19D2000T", label: "Slotted Strut Galv 40 x 40 x 1.6mm", category: "Strut", stockLength: 6000 },
  { code: "19D3300", label: "Plain Strut Galv 40 x 20 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D3300HDG", label: "Plain Strut HDG 40 x 20 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D3300SS", label: "Plain Strut S/S 40 x 20 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D3300T", label: "Slotted Strut Galv 40 x 20 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D3300THDG", label: "Slotted Strut HDG 40 x 20 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D3300TSS", label: "Slotted Strut S/S 40 x 20 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D4000", label: "Plain Strut Galv 40 x 20 x 1.6mm", category: "Strut", stockLength: 6000 },
  { code: "19D4000T", label: "Slotted Strut Galv 40 x 20 x 1.6mm", category: "Strut", stockLength: 6000 },
  { code: "19D5500", label: "Plain Strut Galv 40 x 60 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D5500HDG", label: "Plain Strut HDG 40 x 60 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D5500T", label: "Slotted Strut Galv 40 x 60 x 2.5mm", category: "Strut", stockLength: 6000 },
  { code: "19D5500THDG", label: "Slotted Strut HDG 40 x 60 x 2.5mm", category: "Strut", stockLength: 6000 },
  // Angle
  { code: "14GA3030SL", label: "Slotted Angle 30 x 30 x 2.5mm", category: "Angle", stockLength: 3000 },
  { code: "14GA4040SL", label: "Slotted Angle 40 x 40 x 2.5mm", category: "Angle", stockLength: 3000 },
  { code: "14GA5050SL6", label: "Slotted Angle 50 x 50 x 5.0mm", category: "Angle", stockLength: 6000 },
  { code: "14GA6565SL6", label: "Slotted Angle 65 x 65 x 5.0mm", category: "Angle", stockLength: 6000 },
  // Allthread
  { code: "13TRM083", label: "M8x3mtr Allthread", category: "Allthread", stockLength: 3000 },
  { code: "13TRM083SS316", label: "M8x3mtr Allthread S/S 316", category: "Allthread", stockLength: 3000 },
  { code: "13TRM103", label: "M10x3mtr Allthread", category: "Allthread", stockLength: 3000 },
  { code: "13TRM103G", label: "M10x3mtr Allthread HDG", category: "Allthread", stockLength: 3000 },
  { code: "13TRM103SS316", label: "M10x3mtr Allthread S/S 316", category: "Allthread", stockLength: 3000 },
  { code: "13TRM123", label: "M12x3mtr Allthread", category: "Allthread", stockLength: 3000 },
  { code: "13TRM12388G", label: "M12x3mtr 8.8 High Tensile Allthread HDG", category: "Allthread", stockLength: 3000 },
  { code: "13TRM123G", label: "M12x3mtr Allthread HDG", category: "Allthread", stockLength: 3000 },
  { code: "13TRM123SS316", label: "M12x3mtr Allthread S/S 316", category: "Allthread", stockLength: 3000 },
  { code: "13TRM163", label: "M16x3mtr Allthread", category: "Allthread", stockLength: 3000 },
  { code: "13TRM16388G", label: "M16x3mtr 8.8 High Tensile Allthread HDG", category: "Allthread", stockLength: 3000 },
  { code: "13TRM163G", label: "M16x3mtr Allthread HDG", category: "Allthread", stockLength: 3000 },
  { code: "13TRM163SS316", label: "M16x3mtr Allthread S/S 316", category: "Allthread", stockLength: 3000 },
];

export const CATEGORIES = ["Mega", "Strut", "Angle", "Allthread"] as const;

// ── FFD Algorithm ─────────────────────────────────────────────────────────────

export function firstFitDecreasing(
  pieces: CutPiece[],
  stockLen: number,
  kerf: number
): StockBar[] | null {
  const stocks: StockBar[] = [];
  const sorted = [...pieces].sort((a, b) => b.len - a.len);
  for (const p of sorted) {
    let placed = false;
    for (const s of stocks) {
      const used = s.cuts.reduce((acc, c) => acc + c.len + kerf, 0);
      // Reserve a kerf for the new piece too, matching the per-cut kerf
      // model used by calcYield so reported waste can never go negative.
      // +0.001 tolerates float noise so exact fits (incl. zero-kerf) pack.
      if (used + p.len + kerf <= stockLen + 0.001) {
        s.cuts.push(p);
        placed = true;
        break;
      }
    }
    if (!placed) {
      if (p.len <= stockLen) stocks.push({ cuts: [p] });
      else return null;
    }
  }
  return stocks;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

export function calcYield(
  pieces: CutPiece[],
  stocks: StockBar[],
  stockLen: number,
  kerf: number
) {
  const totalPiece = pieces.reduce((a, p) => a + p.len, 0);
  const totalStock = stocks.length * stockLen;
  const totalKerfs = stocks.reduce((a, s) => a + s.cuts.length * kerf, 0);
  const totalWaste = totalStock - totalPiece - totalKerfs;
  const yieldPct = (totalPiece / totalStock) * 100;
  return { totalPiece, totalStock, totalKerfs, totalWaste, yieldPct };
}

export function consolidateCutDefs(rawDefs: CutDef[]): CutDef[] {
  const consolidated: CutDef[] = [];
  for (const d of rawDefs) {
    const existing = consolidated.find(c => c.len === d.len);
    if (existing) {
      existing.qty += d.qty;
    } else {
      consolidated.push({ len: d.len, qty: d.qty });
    }
  }
  return consolidated;
}

/** Group stock bars that share an identical cut pattern (same lengths in order). */
export function consolidatePatterns(
  stocks: StockBar[]
): { stock: StockBar; count: number }[] {
  const consolidated: { stock: StockBar; count: number }[] = [];
  const patternMap = new Map<string, number>();
  for (const stock of stocks) {
    const key = stock.cuts.map(c => c.len).join(",");
    const existingIdx = patternMap.get(key);
    if (existingIdx !== undefined) {
      consolidated[existingIdx]!.count++;
    } else {
      patternMap.set(key, consolidated.length);
      consolidated.push({ stock, count: 1 });
    }
  }
  return consolidated;
}

export function colorFor(len: number, uniqueLens: number[]): string {
  return COLORS[uniqueLens.indexOf(len) % COLORS.length] ?? "#1B449C";
}
export function lightColorFor(len: number, uniqueLens: number[]): string {
  return LIGHT_COLORS[uniqueLens.indexOf(len) % LIGHT_COLORS.length] ?? "#E6ECF6";
}

/** Format ISO date (YYYY-MM-DD) to Australian DD/MM/YYYY */
export function fmtDateAU(iso: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}/${m}/${y}` : iso;
}
